import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useAdminOrders, useInventoryData, useDailySalesSummary } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime, getStatusColor, getStatusLabel } from '../../lib/utils';
import { ShoppingBag, Clock, CheckCircle2, AlertTriangle, Play, Pause } from 'lucide-react';
import { Button } from '../../components/ui/Button';
export function DashboardPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    orders,
    loading: ordersLoading,
    updateOrderStatus
  } = useAdminOrders(restaurant?.id);
  const {
    summary,
    loading: salesLoading
  } = useDailySalesSummary(restaurant?.id);
  const {
    alerts,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  useEffect(() => {
    if (settings) {
      console.log('[NOIZA][Dashboard] Settings loaded, kitchen_paused =', settings.kitchen_paused, 'restaurant_id =', restaurant?.id);
      setKitchenPaused(settings.kitchen_paused);
    }
  }, [settings, restaurant?.id]);
  const handleToggleKitchen = async () => {
    if (!restaurant) {
      console.error('[NOIZA][Dashboard] No restaurant loaded — cannot toggle');
      return;
    }
    setTogglingKitchen(true);
    setToggleError(null);
    const newValue = !kitchenPaused;
    console.log('[NOIZA][Dashboard] Toggling kitchen:', {
      restaurantId: restaurant.id,
      slug,
      currentValue: kitchenPaused,
      newValue
    });
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, newValue);
    if (result.success) {
      setKitchenPaused(result.newValue);
      setToggleError(null);
    } else {
      // Revert to DB-confirmed value
      setKitchenPaused(result.newValue);
      setToggleError(result.error || 'No se pudo actualizar el estado de cocina');
      console.error('[NOIZA][Dashboard] Toggle FAILED:', result.error);
    }
    setTogglingKitchen(false);
  };
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1957">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-1958" />
      </div>;
  }
  const todayOrdersCount = summary?.orders_today || 0;
  const todayRevenue = summary?.total_today || 0;
  const newOrders = orders.filter(o => o.status === 'new');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');
  // Derive alert counts from row-level v_inventory_alerts (real view uses uppercase values)
  const criticalAlerts = alerts.filter(a => a.alert_level === 'CRITICAL');
  const lowAlerts = alerts.filter(a => a.alert_level === 'LOW');
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1959">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-1960">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1961">
          <div data-id="element-1962">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1963">Dashboard</h1>
            <p className="text-gray-500" data-id="element-1964">Resumen de operaciones de hoy</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-1965">
            <button onClick={handleToggleKitchen} disabled={togglingKitchen} className={`flex items-center gap-2 px-4 py-2 rounded-xl border shadow-sm transition-all active:scale-[0.98] ${togglingKitchen ? 'opacity-50 pointer-events-none' : ''} ${kitchenPaused ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-1966">
              <span className="text-sm font-medium text-gray-600" data-id="element-1967">Cocina:</span>
              {kitchenPaused ? <Badge variant="danger" className="flex items-center gap-1" data-id="element-1968">
                  <Pause className="w-3 h-3" data-id="element-1969" /> Pausada
                </Badge> : <Badge variant="success" className="flex items-center gap-1" data-id="element-1970">
                  <Play className="w-3 h-3" data-id="element-1971" /> Activa
                </Badge>}
            </button>
          </div>
        </div>

        {/* Toggle Error */}
        {toggleError && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-1972">
            <p className="text-red-700 text-sm font-medium" data-id="element-1973">⚠️ {toggleError}</p>
            <button onClick={() => setToggleError(null)} className="text-red-400 hover:text-red-600 text-xs font-bold ml-4" data-id="element-1974">
              ✕
            </button>
          </div>}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1975">
          <Card data-id="element-1976">
            <CardContent className="p-6" data-id="element-1977">
              <div className="flex items-center justify-between mb-4" data-id="element-1978">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1979">
                  <ShoppingBag className="w-5 h-5 text-blue-600" data-id="element-1980" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-1981">
                  {todayOrdersCount}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1982">Pedidos Hoy</p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-1983">
                {formatCurrency(todayRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1984">
            <CardContent className="p-6" data-id="element-1985">
              <div className="flex items-center justify-between mb-4" data-id="element-1986">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-1987">
                  <Clock className="w-5 h-5 text-amber-600" data-id="element-1988" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-1989">
                  {newOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1990">
                Nuevos / Pendientes
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1991">
            <CardContent className="p-6" data-id="element-1992">
              <div className="flex items-center justify-between mb-4" data-id="element-1993">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-1994">
                  <ChefHatIcon className="w-5 h-5 text-purple-600" data-id="element-1995" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-1996">
                  {preparingOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1997">
                En Preparación
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1998">
            <CardContent className="p-6" data-id="element-1999">
              <div className="flex items-center justify-between mb-4" data-id="element-2000">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-2001">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-2002" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-2003">
                  {readyOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-2004">
                Listos para Entrega
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2005">
          {/* Recent Orders */}
          <Card className="lg:col-span-2" data-id="element-2006">
            <CardHeader data-id="element-2007">
              <CardTitle data-id="element-2008">Pedidos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-2009">
              {ordersLoading ? <div className="space-y-4" data-id="element-2010">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-2011" />)}
                </div> : orders.length > 0 ? <div className="overflow-x-auto" data-id="element-2012">
                  <table className="w-full text-sm text-left" data-id="element-2013">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-2014">
                      <tr data-id="element-2015">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-2016">Pedido</th>
                        <th className="px-4 py-3" data-id="element-2017">Cliente</th>
                        <th className="px-4 py-3" data-id="element-2018">Tipo</th>
                        <th className="px-4 py-3" data-id="element-2019">Total</th>
                        <th className="px-4 py-3" data-id="element-2020">Estado</th>
                        <th className="px-4 py-3 rounded-r-lg" data-id="element-2021">Acción</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2022">
                      {orders.slice(0, 10).map(order => <tr key={order.id} className="border-b border-gray-50 last:border-0" data-id="element-2023">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-2024">
                            #{order.order_number}
                            <div className="text-xs text-gray-500 font-normal" data-id="element-2025">
                              {formatRelativeTime(order.created_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-2026">{order.customer_name}</td>
                          <td className="px-4 py-3" data-id="element-2027">
                            <Badge variant="default" data-id="element-2028">
                              {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para llevar' : 'Mesa'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-medium" data-id="element-2029">
                            {formatCurrency(order.total)}
                          </td>
                          <td className="px-4 py-3" data-id="element-2030">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`} data-id="element-2031">
                              {getStatusLabel(order.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-2032">
                            {order.status === 'new' && <Button size="sm" onClick={() => updateOrderStatus(order.id, 'accepted')} data-id="element-2033">
                                Aceptar
                              </Button>}
                            {order.status === 'accepted' && <Button size="sm" variant="secondary" onClick={() => updateOrderStatus(order.id, 'preparing')} data-id="element-2034">
                                Preparar
                              </Button>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div> : <div className="text-center py-8 text-gray-500" data-id="element-2035">
                  No hay pedidos recientes
                </div>}
            </CardContent>
          </Card>

          {/* Alerts & Info */}
          <div className="space-y-6" data-id="element-2036">
            <Card data-id="element-2037">
              <CardHeader data-id="element-2038">
                <CardTitle className="flex items-center gap-2" data-id="element-2039">
                  <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-2040" />
                  Alertas de Inventario
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-2041">
                {invLoading ? <Skeleton className="h-20 w-full" data-id="element-2042" /> : alerts.length > 0 ? <div className="space-y-4" data-id="element-2043">
                    {criticalAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-2044">
                        <span className="text-sm font-medium text-red-800" data-id="element-2045">
                          Stock Crítico
                        </span>
                        <Badge variant="danger" data-id="element-2046">{criticalAlerts.length}</Badge>
                      </div>}
                    {lowAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-2047">
                        <span className="text-sm font-medium text-amber-800" data-id="element-2048">
                          Stock Bajo
                        </span>
                        <Badge variant="warning" data-id="element-2049">{lowAlerts.length}</Badge>
                      </div>}
                    <div className="space-y-2 mt-2" data-id="element-2050">
                      {alerts.slice(0, 5).map(alert => <div key={alert.inventory_item_id} className="flex justify-between text-sm" data-id="element-2051">
                          <span className="text-gray-700 truncate" data-id="element-2052">
                            {alert.ingredient_name}
                          </span>
                          <span className="text-gray-500 shrink-0 ml-2" data-id="element-2053">
                            {alert.current_stock} / {alert.minimum_stock}{' '}
                            {alert.unit}
                          </span>
                        </div>)}
                    </div>
                  </div> : <p className="text-sm text-gray-500" data-id="element-2054">
                    No hay alertas de inventario.
                  </p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>;
}
// Helper icon
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2055">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-2056" />
      <path d="M6 17h12" data-id="element-2057" />
    </svg>;
}